import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  UpdateProfileBody,
  UpdateProfileData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface UpdateProfileState
  extends MergeRequestState<UpdateProfileData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.updateProfile,
  isFetching: (state: ReduxState) => state.server.updateProfile.isFetching,
  data: (state: ReduxState) => state.server.updateProfile.data,
  error: (state: ReduxState) => state.server.updateProfile.error,
};

const initialState: UpdateProfileState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/updateProfile';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    { body, config }: { body: UpdateProfileBody; config?: ServerApiConfig },
    { rejectWithValue },
  ) => serverApi.updateProfile(body, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerUpdateProfile {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const updateProfile: ServerUpdateProfile = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
